import { useMediaQuery } from '@material-ui/core'
import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  BaseThemeType,
  CheckOutlineIcon,
  ChevronDownOutlineIcon,
  styled,
  Typography,
} from 'opensolar-ui'
import { useEffect } from 'react'
import { CheckoutFinancingPricingOption } from '../types'
import FinanceMarketplaceBreakdownTableRow from './FinanceMarketplaceBreakdownTableRow'

type PropTypes = {
  pricingOption: CheckoutFinancingPricingOption
  rank?: number
}

const StyledAccordion = styled(Accordion)(() => ({
  width: '100%',
  '& .MuiAccordionDetails-root': {
    width: '100%',
    maxWidth: 'unset',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
}))

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}))

const StyleAccordionDetails = styled(AccordionDetails)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}))

const AccordionContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '90%',
}))

const BreakdownTable = styled('div')(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
  padding: isMobile ? '0px' : '12px 0px',
}))

const BulletList = styled('div')(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
  padding: isMobile ? '12px 0px' : '12px 24px',
}))
const BulletRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
}))

const StyledCheckOutlineIcon = styled(CheckOutlineIcon)(({ theme }) => ({
  width: '16px',
  height: '16px',
  marginRight: '6px',
  color: theme.palette.green[800],
}))

const DisclaimerWrapper = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: isMobile ? '12px' : '12px 24px',
    backgroundColor: theme.palette.grey[100],
    marginLeft: isMobile ? '0px' : '20px',
    marginTop: '8px',
  })
)

const FinanceMarketplacePaymentsBreakdown = (props: PropTypes) => {
  const { pricingOption } = props
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  useEffect(() => {
    logAmplitudeEventAllowCustomers('marketplace_pricing_option_breakdown_expanded', {
      rank: props.rank,
      integration: pricingOption.lender_integration_name,
      product_title: pricingOption.product_description,
      product_type: pricingOption.sub_type_display_name,
    })
  }, [])

  return (
    <StyledAccordion>
      <StyledAccordionSummary expandIcon={<ChevronDownOutlineIcon />}>Payment Breakdown</StyledAccordionSummary>
      <StyleAccordionDetails>
        <AccordionContent>
          <BreakdownTable isMobile={isMobile}>
            {pricingOption?.payment_breakdown_elements?.breakdown_table?.map((tableRow, i) => (
              <FinanceMarketplaceBreakdownTableRow
                key={i}
                tableRow={tableRow}
                currencySymbol={pricingOption.currency_symbol}
              />
            ))}
          </BreakdownTable>
          <BulletList isMobile={isMobile}>
            {pricingOption?.payment_breakdown_elements?.bullet_list?.map((bulletItem, i) => (
              <BulletRow key={i}>
                <StyledCheckOutlineIcon />
                <Typography textVariant="body2">{bulletItem}</Typography>
              </BulletRow>
            ))}
          </BulletList>
          {pricingOption?.payment_breakdown_elements?.disclaimer_html && (
            <DisclaimerWrapper isMobile={isMobile}>
              <div dangerouslySetInnerHTML={{ __html: pricingOption.payment_breakdown_elements.disclaimer_html }} />
            </DisclaimerWrapper>
          )}
        </AccordionContent>
      </StyleAccordionDetails>
    </StyledAccordion>
  )
}
export default FinanceMarketplacePaymentsBreakdown
